import type { JSX } from 'react';

import classNames from 'classnames';

import { SingleSelect, type SelectOption } from '../Inputs/SingleSelect';

import styles from './Tabs.module.css';

interface TabOption<T extends string = string> {
  label: string;
  value: T;
  onClick?: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface TabsProps<T extends string = string> extends React.ComponentProps<'div'> {
  options: TabOption<T>[];
  activeTab: T;
  setActiveTab: (tab: T) => void;
  rounded?: boolean;
  fill?: boolean;
  /** Underline tabs wrap onto extra rows instead of scrolling sideways. */
  wrap?: boolean;
  variant?: 'segmented' | 'underline';
}

function Tabs<T extends string = string>({
  className,
  options,
  activeTab,
  setActiveTab,
  rounded = false,
  fill = false,
  wrap = false,
  variant = 'underline',
  ...props
}: TabsProps<T>): JSX.Element {
  const selectOptions: SelectOption[] = options.map((option) => ({
    label: option.label,
    value: option.value,
    disabled: option.disabled,
  }));

  const handleSelectChange = (value: number | string) => {
    const option = options.find((o) => o.value === value);
    if (option && !option.disabled) {
      setActiveTab(option.value);
      option.onClick?.();
    }
  };

  return (
    <div className={classNames(styles.Tabs, className)} {...props}>
      <div
        className={classNames(styles.TabsList, {
          [styles.rounded]: rounded,
          [styles.fill]: fill,
          [styles.underline]: variant === 'underline',
          [styles.wrap]: wrap,
        })}
      >
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            disabled={option.disabled}
            className={classNames(styles.TabsTrigger, {
              [styles.active]: option.value === activeTab,
              [styles.disabled]: option.disabled,
              [styles.rounded]: rounded,
              [styles.fill]: fill,
            })}
            onClick={() => {
              if (!option.disabled) {
                setActiveTab(option.value);
                option.onClick?.();
              }
            }}
          >
            {option.icon && option.icon}
            {option.label}
          </button>
        ))}
      </div>
      <div className={styles.MobileSelect}>
        <SingleSelect
          options={selectOptions}
          value={activeTab}
          onChange={handleSelectChange}
        />
      </div>
    </div>
  );
}

export { Tabs };
